import { useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import {
  DataGrid,
  GridFooterContainer,
  GridPagination,
  GridToolbarContainer,
  GridToolbarColumnsButton,
} from "@mui/x-data-grid";
import { ChevronDown, Columns3, Download, Filter, Search } from "lucide-react";
import { downloadStyledXls } from "./exportXls";
import { api } from "./api";
import AppDataGrid, {
  PersonCell,
  TextCell,
  GridStatusPill,
  gridShellSx,
  useDataGridSx,
} from "./AppDataGrid";

function exportRowsCsv(rows) {
  api.trackDownload("DOWNLOAD_DIRECTORY_EXPORT", `${rows.length} employees`);
  downloadStyledXls(
    "employees",
    "Employee Directory",
    ["Name", "Role", "Department", "Manager", "Work type", "Status"],
    rows.map((r) => [r.name, r.role, r.dept, r.manager, r.type, r.status])
  );
}

const toolbarBtnSx = {
  borderRadius: "12px",
  height: 42,
  fontWeight: 500,
  textTransform: "none",
  borderColor: "divider",
  color: "text.primary",
  px: 1.75,
};

const iconBtnSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "12px",
  width: 42,
  height: 42,
  color: "text.secondary",
};

function EmployeesToolbar({
  search,
  onSearchChange,
  dept,
  onDeptChange,
  depts,
  status,
  onStatusChange,
  statuses,
  onAdd,
  rows,
}) {
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [quickAnchor, setQuickAnchor] = useState(null);

  return (
    <GridToolbarContainer
      sx={{
        px: 2,
        py: 1.5,
        gap: 1,
        alignItems: "center",
        flexWrap: "nowrap",
        bgcolor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
        overflowX: "auto",
        overflowY: "hidden",
        flexShrink: 0,
      }}
    >
      <Button
        variant="contained"
        onClick={onAdd}
        sx={{ borderRadius: "12px", height: 42, px: 2, flexShrink: 0, whiteSpace: "nowrap" }}
      >
        Create
      </Button>

      <TextField
        size="small"
        variant="outlined"
        placeholder="Search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{
          width: 220,
          maxWidth: 220,
          flexShrink: 0,
          "& .MuiOutlinedInput-root": {
            height: 42,
            borderRadius: "12px",
            pl: 1.25,
            bgcolor: "background.paper",
            alignItems: "center",
            "& fieldset": { borderColor: "divider" },
          },
          "& .MuiOutlinedInput-input": {
            py: 0,
            px: 0.5,
            height: "auto",
          },
          "& .MuiInputAdornment-root": {
            height: "auto",
            maxHeight: "none",
            alignSelf: "center",
            mr: 0.5,
            color: "text.secondary",
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={16} />
            </InputAdornment>
          ),
        }}
      />

      <Button
        variant="outlined"
        startIcon={<Filter size={16} />}
        endIcon={<ChevronDown size={14} />}
        onClick={(e) => setFilterAnchor(e.currentTarget)}
        sx={{ ...toolbarBtnSx, flexShrink: 0 }}
      >
        Filters
      </Button>
      <Menu anchorEl={filterAnchor} open={Boolean(filterAnchor)} onClose={() => setFilterAnchor(null)}>
        {depts.map((d) => (
          <MenuItem key={d} selected={dept === d} onClick={() => { onDeptChange(d); setFilterAnchor(null); }}>
            {d === "All" ? "All departments" : d}
          </MenuItem>
        ))}
      </Menu>

      <Button
        variant="outlined"
        endIcon={<ChevronDown size={14} />}
        onClick={(e) => setQuickAnchor(e.currentTarget)}
        sx={{ ...toolbarBtnSx, flexShrink: 0 }}
      >
        {status === "All" ? "Quick filter" : status}
      </Button>
      <Menu anchorEl={quickAnchor} open={Boolean(quickAnchor)} onClose={() => setQuickAnchor(null)}>
        {statuses.map((s) => (
          <MenuItem key={s} selected={status === s} onClick={() => { onStatusChange(s); setQuickAnchor(null); }}>
            {s === "All" ? "All statuses" : s}
          </MenuItem>
        ))}
      </Menu>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto", flexShrink: 0 }}>
        <GridToolbarColumnsButton
          slotProps={{
            button: {
              variant: "outlined",
              className: "toolbar-column-button",
              startIcon: <Columns3 size={16} />,
              sx: { ...toolbarBtnSx, flexShrink: 0 },
            },
            tooltip: { title: "Columns" },
          }}
        />
        <IconButton
          onClick={() => exportRowsCsv(rows)}
          className="toolbar-export-button toolbar-icon-buttons"
          sx={iconBtnSx}
          aria-label="Export"
        >
          <Download size={18} />
        </IconButton>
      </Box>
    </GridToolbarContainer>
  );
}

function EmployeesFooter({ rowCount }) {
  return function Footer() {
    return (
      <GridFooterContainer
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          minHeight: 48,
          px: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          Total Rows: {rowCount}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <GridPagination />
      </GridFooterContainer>
    );
  };
}

export default function EmployeesDataGrid({ employees, onRowClick, onAdd, onDisable, onRestore }) {
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("All");
  const [status, setStatus] = useState("All");
  const gridSx = useDataGridSx({ fillHeight: true });

  const depts = useMemo(
    () => ["All", ...Array.from(new Set(employees.map((e) => e.dept).filter(Boolean)))],
    [employees]
  );

  const statuses = useMemo(
    () => ["All", ...Array.from(new Set(employees.map((e) => e.status).filter(Boolean)))],
    [employees]
  );

  const rows = useMemo(
    () =>
      employees
        .filter((e) => {
          const matchDept = dept === "All" || e.dept === dept;
          const matchStatus = status === "All" || e.status === status;
          const q = search.toLowerCase();
          const matchSearch = !search ||
            e.name.toLowerCase().includes(q) ||
            e.role.toLowerCase().includes(q) ||
            e.dept?.toLowerCase().includes(q) ||
            e.manager?.toLowerCase().includes(q);
          return matchDept && matchStatus && matchSearch;
        })
        .map((e) => ({ ...e })),
    [employees, dept, status, search]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Employee",
        flex: 1.15,
        minWidth: 220,
        renderCell: ({ row }) => <PersonCell name={row.name} manager={row.isManager} avatar={row.avatar} />,
      },
      {
        field: "dept",
        headerName: "Department",
        flex: 0.75,
        minWidth: 120,
        renderCell: ({ value }) => <TextCell value={value} />,
      },
      {
        field: "manager",
        headerName: "Manager",
        flex: 0.9,
        minWidth: 170,
        renderCell: ({ value }) => <PersonCell name={value} />,
      },
      {
        field: "type",
        headerName: "Work type",
        flex: 0.65,
        minWidth: 110,
        renderCell: ({ value }) => <TextCell value={value} />,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.7,
        minWidth: 115,
        renderCell: ({ value }) => <GridStatusPill status={value} />,
      },
      {
        field: "actions",
        headerName: "",
        width: 110,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) =>
          row.status === "Inactive" ? (
            onRestore ? (
              <Button
                size="small"
                onClick={(e) => { e.stopPropagation(); onRestore?.(row); }}
                sx={{ textTransform: "none", color: "primary.main", minWidth: 0, px: 1, fontWeight: 600 }}
              >
                Reactivate
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">Inactive</Typography>
            )
          ) : (
            <Button
              size="small"
              onClick={(e) => { e.stopPropagation(); onDisable?.(row); }}
              sx={{ textTransform: "none", color: "error.main", minWidth: 0, px: 1, fontWeight: 500 }}
            >
              Deactivate
            </Button>
          ),
      },
    ],
    [onDisable, onRestore]
  );

  return (
    <Box sx={{ ...gridShellSx, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        density="comfortable"
        columnHeaderHeight={44}
        disableRowSelectionOnClick
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
        }}
        onRowClick={({ row }) => onRowClick?.(row.id)}
        slots={{
          toolbar: EmployeesToolbar,
          footer: EmployeesFooter(rows.length),
        }}
        slotProps={{
          toolbar: {
            search,
            onSearchChange: setSearch,
            dept,
            onDeptChange: setDept,
            depts,
            status,
            onStatusChange: setStatus,
            statuses,
            onAdd,
            rows,
          },
        }}
        sx={{
          ...gridSx,
          flex: 1,
          minHeight: 0,
          height: "100%",
        }}
      />
    </Box>
  );
}
